import React, { useEffect, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { css } from '@emotion/css';
import PageContainer from '../../components/structure/PageContainer';
import SettingsCard from '../../components/structure/SettingsCard';
import CustomScrollbars from '../../components/structure/CustomScrollbars';
import Loader from '../../components/general/Loader';
import OrganizationModel from '../../models/organizations/api/OrganizationModel';
import * as voicifyApi from '../../api';
import { FoodOrderingFeatureFlagId } from '../../constants/featureFlagIds';

const CURRENT_ORG_KEY = 'CURRENT_ORG';

const DeepAnalysisUpsellValues: React.FC = () => {
    const [organizations, setOrganizations] = useState<OrganizationModel[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
    const [loadedOrganizationId, setLoadedOrganizationId] = useState('');
    const [values, setValues] = useState<voicifyApi.DeepAnalysisUpsellValuesByTag>({});
    const [isAdminVerified, setIsAdminVerified] = useState<boolean | null>(null);
    const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
    const [isLoadingValues, setIsLoadingValues] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;
        const verifyAdmin = async () => {
            try {
                const result = await voicifyApi.getTagConfig('SPANISH', FoodOrderingFeatureFlagId);
                if (!cancelled) setIsAdminVerified(result.resultType === 'Ok');
            } catch {
                if (!cancelled) setIsAdminVerified(false);
            }
        };
        verifyAdmin();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const loadOrganizations = async () => {
            setIsLoadingOrganizations(true);
            setError(null);
            try {
                const result = await voicifyApi.getOrganizations();
                if (result.resultType === 'Ok' && result.data) {
                    const sortedOrganizations = result.data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    setOrganizations(sortedOrganizations);

                    const storedOrg = localStorage.getItem(CURRENT_ORG_KEY);
                    if (storedOrg && storedOrg !== 'undefined') {
                        try {
                            const parsed = JSON.parse(storedOrg);
                            if (parsed?.id && sortedOrganizations.some(org => org.id === parsed.id)) {
                                setSelectedOrganizationId(parsed.id);
                            }
                        } catch {
                        }
                    }
                } else {
                    setError('Failed to load organizations.');
                }
            } catch {
                setError('An error occurred while loading organizations.');
            }
            setIsLoadingOrganizations(false);
        };

        if (isAdminVerified) loadOrganizations();
    }, [isAdminVerified]);

    const sortedRows = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return Object.keys(values)
            .filter(tag => !normalizedSearch || tag.toLowerCase().includes(normalizedSearch) || (values[tag]?.displayName || '').toLowerCase().includes(normalizedSearch))
            .sort((a, b) => a.localeCompare(b));
    }, [values, search]);

    const hasLoadedSelectedOrganization = !!selectedOrganizationId && selectedOrganizationId === loadedOrganizationId;

    const onOrganizationChange = (organizationId: string) => {
        setSelectedOrganizationId(organizationId);
        setLoadedOrganizationId('');
        setValues({});
        setSearch('');
        setError(null);
        setSuccessMessage(null);
    };

    const loadValues = async () => {
        if (!selectedOrganizationId) {
            setError('Choose an organization first.');
            return;
        }

        const organizationId = selectedOrganizationId;
        setIsLoadingValues(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const result = await voicifyApi.syncDeepAnalysisUpsellValues(organizationId);
            if (result.resultType === 'Ok' && result.data) {
                setValues(result.data);
                setLoadedOrganizationId(organizationId);
                setSuccessMessage('Upsell values synced.');
            } else {
                setError('Failed to sync upsell values.');
                setLoadedOrganizationId('');
            }
        } catch {
            setError('An error occurred while syncing upsell values.');
            setLoadedOrganizationId('');
        }
        setIsLoadingValues(false);
    };

    const saveValues = async () => {
        if (!selectedOrganizationId) {
            setError('Choose an organization first.');
            return;
        }
        if (!hasLoadedSelectedOrganization) {
            setError('Load / Sync values for the selected organization before saving.');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const result = await voicifyApi.updateDeepAnalysisUpsellValues(selectedOrganizationId, values);
            if (result.resultType === 'Ok' && result.data) {
                setValues(result.data);
                setLoadedOrganizationId(selectedOrganizationId);
                setSuccessMessage('Upsell values saved.');
            } else {
                setError('Failed to save upsell values.');
            }
        } catch {
            setError('An error occurred while saving upsell values.');
        }
        setIsSaving(false);
    };

    const updateTagValue = (tag: string, patch: Partial<voicifyApi.DeepAnalysisUpsellValue>) => {
        setValues(current => ({
            ...current,
            [tag]: {
                dollarValue: current[tag]?.dollarValue || 0,
                displayName: current[tag]?.displayName || '',
                hidden: current[tag]?.hidden ?? false,
                ...patch
            }
        }));
    };

    if (isAdminVerified === null) {
        return <PageContainer><div className={centeredStyle}><Loader /></div></PageContainer>;
    }

    if (!isAdminVerified) {
        return <Redirect to="/v" />;
    }

    return (
        <PageContainer>
            <CustomScrollbars className={scrollStyle}>
                <div className={pageStyle}>
                    <h3 className={titleStyle}>Deep Analysis Upsell Values</h3>
                    <p className={descriptionStyle}>Manage dollar values, display names, and visibility for normalized upsell tags.</p>

                    <SettingsCard title="Organization" subtitle="Sync tags from the registry and edit their value metadata">
                        {error && <div className={errorStyle}>{error}</div>}
                        {successMessage && <div className={successStyle}>{successMessage}</div>}

                        <div className={toolbarStyle}>
                            <select
                                className={selectStyle}
                                value={selectedOrganizationId}
                                disabled={isLoadingOrganizations || isLoadingValues || isSaving}
                                onChange={(e) => onOrganizationChange(e.target.value)}>
                                <option value="">Choose organization...</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>{org.name || org.id}</option>
                                ))}
                            </select>
                            <button className={buttonStyle} disabled={!selectedOrganizationId || isLoadingValues || isSaving} onClick={loadValues}>
                                {isLoadingValues ? 'Syncing...' : 'Load / Sync'}
                            </button>
                            <button className={primaryButtonStyle} disabled={!hasLoadedSelectedOrganization || isLoadingValues || isSaving} onClick={saveValues}>
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>

                        {selectedOrganizationId && !hasLoadedSelectedOrganization && !isLoadingValues && (
                            <div className={noticeStyle}>Load / Sync this organization before saving changes.</div>
                        )}

                        <input
                            className={searchStyle}
                            placeholder="Filter tags..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)} />

                        <div className={tableWrapStyle}>
                            <table className={tableStyle}>
                                <thead>
                                    <tr>
                                        <th>Tag</th>
                                        <th>Dollar Value</th>
                                        <th>Display Name</th>
                                        <th>Hidden</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map(tag => (
                                        <tr key={tag}>
                                            <td className={tagCellStyle}>{tag}</td>
                                            <td>
                                                <input
                                                    className={numberInputStyle}
                                                    type="number"
                                                    step="0.01"
                                                    value={values[tag]?.dollarValue ?? 0}
                                                    disabled={values[tag]?.hidden === true}
                                                    onChange={(e) => updateTagValue(tag, { dollarValue: parseFloat(e.target.value) || 0 })} />
                                            </td>
                                            <td>
                                                <input
                                                    className={textInputStyle}
                                                    type="text"
                                                    value={values[tag]?.displayName || ''}
                                                    onChange={(e) => updateTagValue(tag, { displayName: e.target.value })} />
                                            </td>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Hide ${tag} upsell`}
                                                    checked={values[tag]?.hidden ?? false}
                                                    onChange={(e) => updateTagValue(tag, {
                                                        hidden: e.target.checked,
                                                        dollarValue: e.target.checked ? 0 : values[tag]?.dollarValue ?? 0
                                                    })} />
                                            </td>
                                        </tr>
                                    ))}
                                    {!sortedRows.length && (
                                        <tr><td colSpan={4} className={emptyStyle}>No upsell values loaded.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </SettingsCard>
                </div>
            </CustomScrollbars>
        </PageContainer>
    );
};

const pageStyle = css`max-width: 1100px; margin: 0 auto; padding: 32px 24px;`;
const centeredStyle = css`display: flex; justify-content: center; padding: 80px;`;
const scrollStyle = css`height: calc(100vh - 60px);`;
const titleStyle = css`margin: 0 0 8px; font-size: 26px; font-weight: 700; color: #1f2937;`;
const descriptionStyle = css`margin: 0 0 24px; color: #4b5563;`;
const toolbarStyle = css`display: flex; gap: 12px; align-items: center; margin-bottom: 16px; flex-wrap: wrap;`;
const selectStyle = css`min-width: 320px; padding: 9px 10px; border: 1px solid #cbd5e1; border-radius: 6px;`;
const buttonStyle = css`padding: 9px 14px; border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; cursor: pointer; &:disabled { opacity: .6; cursor: not-allowed; }`;
const primaryButtonStyle = css`${buttonStyle}; background: #2563eb; color: #fff; border-color: #2563eb;`;
const searchStyle = css`width: 100%; padding: 9px 10px; border: 1px solid #cbd5e1; border-radius: 6px; margin-bottom: 16px;`;
const tableWrapStyle = css`max-height: 620px; overflow: auto; border: 1px solid #e5e7eb; border-radius: 8px;`;
const tableStyle = css`width: 100%; border-collapse: collapse; th, td { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; text-align: left; } th { position: sticky; top: 0; background: #f8fafc; z-index: 1; }`;
const tagCellStyle = css`font-family: monospace; font-size: 13px; color: #111827;`;
const numberInputStyle = css`width: 120px; padding: 7px 8px; border: 1px solid #cbd5e1; border-radius: 5px;`;
const textInputStyle = css`width: 100%; padding: 7px 8px; border: 1px solid #cbd5e1; border-radius: 5px;`;
const errorStyle = css`padding: 10px 12px; background: #fee2e2; color: #991b1b; border-radius: 6px; margin-bottom: 12px;`;
const successStyle = css`padding: 10px 12px; background: #dcfce7; color: #166534; border-radius: 6px; margin-bottom: 12px;`;
const noticeStyle = css`padding: 10px 12px; background: #eff6ff; color: #1d4ed8; border-radius: 6px; margin-bottom: 12px;`;
const emptyStyle = css`text-align: center; color: #6b7280; padding: 24px !important;`;

export default DeepAnalysisUpsellValues;
